import React, { useState } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Lecteur vidéo robuste avec gestion d'erreur :
 * - preload="metadata" pour éviter de décoder tout le fichier d'un coup
 * - playsInline (lecture inline mobile, indispensable iOS)
 * - controls (toujours visible)
 * - onError → affiche un message clair au lieu de pixels corrompus
 *
 * Props:
 *   src: URL de la vidéo
 *   cover: URL de la miniature (optionnel)
 *   className: classes Tailwind pour le wrapper
 */
export const VideoElement = ({ src, cover = null, className = "w-full h-full object-cover" }) => {
  const [errored, setErrored] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const retry = () => {
    setErrored(false);
    setRetryKey((k) => k + 1);
  };

  if (errored) {
    return (
      <div
        className="relative w-full h-full bg-gradient-to-br from-zinc-900 via-zinc-950 to-black flex flex-col items-center justify-center text-center px-6 py-8"
        data-testid="video-error-fallback"
      >
        {cover && (
          <img
            src={cover}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25 blur-md"
            aria-hidden="true"
          />
        )}
        <div className="relative z-10 max-w-sm">
          <div className="inline-flex w-14 h-14 rounded-full bg-red-500/15 text-red-300 items-center justify-center mb-3 border border-red-500/30">
            <AlertTriangle size={24} />
          </div>
          <p className="text-sm font-bold text-white mb-1">Vidéo illisible sur ce navigateur</p>
          <p className="text-xs text-white/55 leading-relaxed mb-4">
            Le format de cette vidéo n'est pas supporté (souvent un codec HEVC/H.265 ou
            un MP4 mal optimisé venant d'un téléphone). L'auteur·rice peut la convertir en
            <span className="text-white/85"> MP4 H.264</span> avec une appli gratuite comme VLC, HandBrake ou InShot, puis la republier.
          </p>
          <button
            type="button"
            onClick={retry}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-widest transition"
            data-testid="video-retry-btn"
          >
            <RotateCcw size={12} /> Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <video
      key={retryKey}
      controls
      preload="metadata"
      playsInline
      poster={cover || undefined}
      src={src}
      onError={() => setErrored(true)}
      className={className}
      data-testid="track-video-player"
    >
      Ton navigateur ne supporte pas la lecture vidéo.
    </video>
  );
};

export default VideoElement;
